import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  Chip,
  Rating,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { addDays, startOfDay, format } from 'date-fns';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import AvailabilityCalendar from '../components/booking/AvailabilityCalendar';
import BookingForm from '../components/booking/BookingForm';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import { businessService } from '../services/businessService';
import { bookingService } from '../services/bookingService';
import { useBooking } from '../hooks/useBooking';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { useAuth } from '../hooks/useAuth';
import { saveUserPhone, getUserProfile } from '../services/userService';

const STEPS = ['Select Service', 'Select Time', 'Your Details', 'Confirmation'];

/**
 * Booking page with multi-step booking flow
 */
const BookingPage = () => {
  const { businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Translated steps
  const translatedSteps = [t('booking.selectService'), t('booking.selectTime'), t('booking.yourDetails'), t('booking.confirmation')];
  
  const customerId = location.state?.customerId;
  const initialCalendarId = location.state?.calendarId;
  const businessFromState = location.state?.business;
  const rebookData = location.state?.rebookData;

  const [activeStep, setActiveStep] = useState(0);
  const [business, setBusiness] = useState(businessFromState || null);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedMeetingType, setSelectedMeetingType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [fetchedDateRange, setFetchedDateRange] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  
  // Ref to track if availability fetch is in progress
  const fetchingAvailability = useRef(false);

  const {
    loading: bookingLoading,
    error: bookingError,
    availableSlots,
    bookingResult,
    searchAvailability,
    bookAppointment,
    rescheduleBooking,
    clearBookingResult,
    clearError,
  } = useBooking();

  // Fetch user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user || !user.email) return;
      
      try {
        const profile = await getUserProfile(user.email);
        if (!profile) {
          console.error('User profile not found for logged-in user');
          setPageError('User profile not found. Please contact support.');
          return;
        }
        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
        setPageError('Failed to load user profile. Please try again.');
      }
    };
    
    loadUserProfile();
  }, [user]);

  // Fetch business and calendar data
  useEffect(() => {
    const fetchData = async () => {
      if (!customerId) {
        setPageError('Customer information missing');
        setPageLoading(false);
        return;
      }

      try {
        // Always fetch full business data to ensure we have contact_info
        const [bizData, calData] = await Promise.all([
          businessService.getBusinessById(customerId, businessId),
          businessService.getCalendars(customerId),
        ]);
        
        setBusiness(bizData);
        setCalendars(calData);

        // Auto-select calendar if specified or not already selected
        if (selectedCalendar) {
          // Don't reset if user already selected a calendar
          return;
        }
        
        if (initialCalendarId) {
          const cal = calData.find(c => c.id === initialCalendarId);
          if (cal) {
            setSelectedCalendar(cal);
          }
        } else if (calData.length === 1) {
          setSelectedCalendar(calData[0]);
        } else {
          // Default to showing all calendars
          setSelectedCalendar({ id: 'ALL', name: t('booking.allCalendars') });
        }
      } catch (err) {
        setPageError(err.message);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [customerId, businessId, initialCalendarId, t, selectedCalendar]);

  // Search availability when calendar, meeting type changes, or fetch longer range (30 days)
  useEffect(() => {
    if (selectedCalendar && selectedMeetingType && activeStep >= 1) {
      // Skip if we already have an error (prevent retry loop)
      if (availabilityError) return;
      
      // Skip if already fetching
      if (fetchingAvailability.current) return;
      
      // Skip if we already fetched this range (prevent duplicate fetches)
      if (fetchedDateRange && 
          selectedDate >= fetchedDateRange.start && 
          selectedDate <= fetchedDateRange.end) {
        return;
      }
      
      const startDate = format(selectedDate, 'yyyy-MM-dd');
      const endDate = format(addDays(selectedDate, 30), 'yyyy-MM-dd');
      
      setAvailabilityError(null);
      fetchingAvailability.current = true;
      
      // Build params - don't include calendar_name if ALL is selected
      const params = {
        start_date: startDate,
        end_date: endDate,
        meeting_type: selectedMeetingType.name,
      };
      
      // Only add calendar_name if not showing all calendars
      if (selectedCalendar.id !== 'ALL') {
        params.calendar_name = selectedCalendar.name || selectedCalendar.id;
      }
      
      searchAvailability(customerId, params).then((result) => {
        // Find the earliest date among all returned slots
        if (result.slots && result.slots.length > 0) {
          // Get all unique dates from slots and find the minimum
          const slotDates = result.slots.map(slot => {
            return startOfDay(new Date(slot.slot_start || slot.start));
          });
          
          // Find the earliest date
          const earliestDate = slotDates.reduce((min, date) => {
            return date < min ? date : min;
          }, slotDates[0]);
          
          // Set selected date to the earliest available date
          setSelectedDate(earliestDate);
          
          // Set fetched range starting from the earliest date
          setFetchedDateRange({ start: earliestDate, end: addDays(earliestDate, 30) });
        } else {
          // No slots found, keep current date
          setFetchedDateRange({ start: selectedDate, end: addDays(selectedDate, 30) });
        }
      }).catch((err) => {
        console.error('Availability error:', err);
        setAvailabilityError(err.message || 'Failed to fetch availability');
      }).finally(() => {
        fetchingAvailability.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendar, selectedMeetingType, activeStep, customerId, selectedDate]);

  // Refetch availability if user selects a date outside the cached range
  useEffect(() => {
    if (selectedCalendar && selectedMeetingType && selectedDate && fetchedDateRange && activeStep >= 1) {
      // Skip if we already have an error
      if (availabilityError) return;
      
      // Skip if already fetching
      if (fetchingAvailability.current) return;
      
      const isOutsideRange = selectedDate < fetchedDateRange.start || selectedDate > fetchedDateRange.end;
      
      if (isOutsideRange) {
        const startDate = format(selectedDate, 'yyyy-MM-dd');
        const endDate = format(addDays(selectedDate, 30), 'yyyy-MM-dd');
        
        fetchingAvailability.current = true;
        
        // Build params - don't include calendar_name if ALL is selected
        const params = {
          start_date: startDate,
          end_date: endDate,
          meeting_type: selectedMeetingType.name,
        };
        
        // Only add calendar_name if not showing all calendars
        if (selectedCalendar.id !== 'ALL') {
          params.calendar_name = selectedCalendar.name || selectedCalendar.id;
        }
        
        searchAvailability(customerId, params).then(() => {
          setFetchedDateRange({ start: selectedDate, end: addDays(selectedDate, 30) });
        }).catch((err) => {
          console.error('Availability refetch error:', err);
          setAvailabilityError(err.message || 'Failed to fetch availability');
        }).finally(() => {
          fetchingAvailability.current = false;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, fetchedDateRange]);

  const handleMeetingTypeSelect = (type) => {
    setSelectedMeetingType(type);
    setAvailabilityError(null); // Clear any previous errors
    
    // If a meeting type from a specific calendar is selected while showing "All",
    // switch to that calendar for availability search
    if (selectedCalendar?.id === 'ALL' && type.calendarId) {
      const cal = calendars.find(c => c.id === type.calendarId);
      if (cal) {
        setSelectedCalendar(cal);
      }
    }
    
    setActiveStep(1);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setActiveStep(2);
  };

  const handleBookingSubmit = async (formData) => {
    if (!selectedSlot || !selectedCalendar || !selectedMeetingType) {
      return;
    }

    try {
      const normalizedPhone = normalizePhoneNumber(formData.phone);
      
      // Use calendar_name from the slot if available (more accurate), otherwise fall back to selectedCalendar
      const calendarNameToBook = selectedSlot.calendar_name || selectedCalendar.name || selectedCalendar.id;
      
      // Check if this is a rebook and if same calendar
      if (rebookData && rebookData.calendarName === calendarNameToBook) {
        // Use rebook endpoint for same calendar
        await rescheduleBooking({
          customer_id: customerId,
          calendar_name: calendarNameToBook,
          event_id: rebookData.eventId,
          new_slot_start: selectedSlot.slot_start || selectedSlot.start,
          new_slot_end: selectedSlot.slot_end || selectedSlot.end,
          timezone: 'Europe/Warsaw'
        });
      } else {
        // If rebooking to different calendar, cancel old and create new
        if (rebookData) {
          try {
            await bookingService.cancelBooking(
              customerId,
              rebookData.calendarName,
              rebookData.eventId
            );
          } catch (cancelErr) {
            console.error('Failed to cancel old booking:', cancelErr);
            // Continue with new booking even if cancel fails
          }
        }
        
        // Create new booking - use calendar_name from slot
        await bookAppointment({
          customer_id: customerId,
          calendar_name: calendarNameToBook,
          slot_start: selectedSlot.slot_start || selectedSlot.start,
          slot_end: selectedSlot.slot_end || selectedSlot.end,
          meeting_type: selectedMeetingType.name,
          customer_name: formData.name,
          customer_email: formData.email,
          customer_telephone: normalizedPhone,
          notes: formData.notes,
          timezone: 'Europe/Warsaw', // Default timezone for Poland
        });
      }
      
      // Save phone number to user profile for future bookings lookup
      if (user && user.email && normalizedPhone) {
        try {
          await saveUserPhone(user.email, normalizedPhone);
        } catch (phoneErr) {
          console.error('Failed to save phone to profile:', phoneErr);
          // Don't fail the booking if profile update fails
        }
      }
      
      setActiveStep(3);
    } catch (err) {
      console.error('Booking error:', err);
      // Error will be handled by the useBooking hook and displayed in the UI
      // The bookingError state is automatically set by the hook
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // If rebooking, go back to My Bookings page, otherwise go to home
      if (rebookData) {
        navigate('/my-bookings');
      } else {
        navigate('/');
      }
    } else {
      // Clear state based on which step we're going back from
      if (activeStep === 2) {
        // Going back from "Your Details" to "Select Time" - clear selected slot
        setSelectedSlot(null);
      } else if (activeStep === 1) {
        // Going back from "Select Time" to "Select Service" - clear meeting type and slot
        setSelectedMeetingType(null);
        setSelectedSlot(null);
      }
      setActiveStep(activeStep - 1);
    }
  };

  const handleBookAnother = () => {
    setActiveStep(0);
    setSelectedMeetingType(null);
    setSelectedSlot(null);
    clearBookingResult();
    clearError();
  };

  if (pageLoading) {
    return <LoadingSpinner message={t('common.loading')} fullPage />;
  }

  if (pageError || !business) {
    return <ErrorDisplay message={pageError || 'Business not found'} fullPage />;
  }

  // Get meeting types from selected calendar or all calendars
  const meetingTypes = selectedCalendar && selectedCalendar.id !== 'ALL'
    ? // Show meeting types from selected calendar
      Object.entries(selectedCalendar.meeting_types || {}).map(([key, value]) => ({
        name: key,
        calendarId: selectedCalendar.id,
        calendarName: selectedCalendar.name || selectedCalendar.id,
        ...value,
      }))
    : // Show all meeting types from all calendars
      calendars.reduce((acc, cal) => {
        if (cal.meeting_types) {
          Object.entries(cal.meeting_types).forEach(([key, value]) => {
            acc.push({
              name: key,
              calendarId: cal.id,
              calendarName: cal.name || cal.id,
              ...value,
            });
          });
        }
        return acc;
      }, []);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Back Button */}
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        {t('common.back')}
      </Button>

      {/* Business Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* Cover Image */}
            {business.branding?.cover_image_url && (
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 120, sm: 160 },
                  borderRadius: 2,
                  mb: { xs: 2, md: 0 },
                  backgroundImage: `url(${business.branding.cover_image_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Contact Details - Same Height as Cover Image */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                height: { xs: 'auto', md: business.branding?.cover_image_url ? 160 : 'auto' },
                justifyContent: 'center',
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                mb: { xs: 2, md: 0 },
              }}
            >
              {business.contact_info?.address && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    flexDirection: 'row',
                  }}
                >
                  <LocationOnIcon 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: 24,
                      flexShrink: 0,
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                      color: 'text.primary',
                      textAlign: { xs: 'left', md: 'right' },
                    }}
                  >
                    {business.contact_info.address}
                  </Typography>
                </Box>
              )}

              {business.contact_info?.phone && (
                <Box 
                  component="a"
                  href={`tel:${business.contact_info.phone}`}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    flexDirection: 'row',
                    '&:hover': {
                      transform: { xs: 'translateX(2px)', md: 'translateX(-2px)' },
                      '& .phone-icon': {
                        color: 'primary.main',
                      }
                    }
                  }}
                >
                  <PhoneIcon 
                    className="phone-icon"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: 24,
                      flexShrink: 0,
                      transition: 'all 0.2s',
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                      color: 'primary.main',
                      fontWeight: 500,
                      textAlign: { xs: 'left', md: 'right' },
                    }}
                  >
                    {business.contact_info.phone}
                  </Typography>
                </Box>
              )}

              {business.contact_info?.email && (
                <Box 
                  component="a"
                  href={`mailto:${business.contact_info.email}`}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    flexDirection: 'row',
                    '&:hover': {
                      transform: { xs: 'translateX(2px)', md: 'translateX(-2px)' },
                      '& .email-icon': {
                        color: 'primary.main',
                      }
                    }
                  }}
                >
                  <EmailIcon 
                    className="email-icon"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: 24,
                      flexShrink: 0,
                      transition: 'all 0.2s',
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-all',
                      lineHeight: 1.6,
                      color: 'primary.main',
                      fontWeight: 500,
                      textAlign: { xs: 'left', md: 'right' },
                    }}
                  >
                    {business.contact_info.email}
                  </Typography>
                </Box>
              )}

              {!business.contact_info?.address && !business.contact_info?.phone && !business.contact_info?.email && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  Contact information not available
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Two Column Layout Below */}
          <Grid item xs={12} md={6}>
            {/* Logo, Name, Category, Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box
                sx={{
                  width: { xs: 60, sm: 70 },
                  height: { xs: 60, sm: 70 },
                  borderRadius: 2,
                  bgcolor: business.branding?.logo_url ? 'transparent' : (business.branding?.primary_color || 'primary.main'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  backgroundImage: business.branding?.logo_url ? `url(${business.branding.logo_url})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!business.branding?.logo_url && (
                  <Typography 
                    variant="h3" 
                    color="white"
                    sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}
                  >
                    {business.business_name?.charAt(0)?.toUpperCase() || 'B'}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography 
                  variant="h5" 
                  component="h1"
                  sx={{ 
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 600,
                    wordBreak: 'break-word',
                    mb: 0.5,
                  }}
                >
                  {business.business_name}
                </Typography>
                {business.category && (
                  <Chip 
                    label={business.category === 'Other' ? t('booking.other') : business.category} 
                    size="small" 
                    sx={{ mb: 0.5 }} 
                  />
                )}
              </Box>
            </Box>

            {business.rating !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Rating value={business.rating} precision={0.5} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">
                  ({business.rating?.toFixed(1)}) · {business.total_bookings || 0} bookings
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Description */}
            {business.description && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '0.938rem' },
                  lineHeight: 1.6,
                  textAlign: { xs: 'center', sm: 'right', md: 'right' },
                }}
              >
                {business.description}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Booking Title */}
      <Typography 
        variant="h5" 
        component="h2"
        sx={{ 
          mb: 3,
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }}
      >
        {t('booking.title')}
      </Typography>

      {/* Stepper */}
      <Stepper 
        activeStep={activeStep} 
        sx={{ 
          mb: 4,
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
          }
        }}
        orientation={isMobile ? 'vertical' : 'horizontal'}
      >
        {translatedSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Show selected meeting type and calendar above breadcrumbs */}
      {activeStep === 1 && selectedMeetingType && selectedCalendar && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('booking.selectedMeetingType')}: <Typography component="span" sx={{ fontWeight: 400 }}>{selectedMeetingType.name}</Typography>
            </Typography>
            {selectedCalendar.id !== 'ALL' && (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t('booking.selectedCalendar')}: <Typography component="span" sx={{ fontWeight: 400 }}>{selectedCalendar.name}</Typography>
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Step Content */}
      {activeStep === 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            {t('business.selectService')}
          </Typography>

          {/* Calendar selector if multiple */}
          {calendars.length > 1 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('booking.calendar')}</InputLabel>
              <Select
                value={selectedCalendar?.id || 'ALL'}
                label={t('booking.calendar')}
                onChange={(e) => {
                  if (e.target.value === 'ALL') {
                    setSelectedCalendar({ id: 'ALL', name: t('booking.allCalendars') });
                  } else {
                    const cal = calendars.find(c => c.id === e.target.value);
                    setSelectedCalendar(cal);
                  }
                }}
              >
                <MenuItem value="ALL">{t('booking.allCalendars')}</MenuItem>
                {calendars.map((cal) => (
                  <MenuItem key={cal.id} value={cal.id}>
                    {cal.name || cal.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Meeting types */}
          <Box sx={{ display: 'grid', gap: 2 }}>
            {meetingTypes.map((type, index) => (
              <Paper
                key={`${type.calendarId}-${type.name}-${index}`}
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => handleMeetingTypeSelect(type)}
              >
                <Typography variant="h6">{type.description || type.name}</Typography>
                {selectedCalendar?.id === 'ALL' && type.calendarName && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('booking.calendar')}: {type.calendarName}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {t('booking.duration')}: {type.duration} {t('booking.minutes')}
                </Typography>
                {type.goal && (
                  <Typography variant="body2" color="text.secondary">
                    {t('booking.purpose')}: {Array.isArray(type.goal) ? type.goal.join(', ') : type.goal}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>
      )}

      {activeStep === 1 && (
        availabilityError ? (
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <ErrorDisplay 
              message={t('booking.noCalendarConfigured')} 
              severity="warning"
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleBack}>
                {t('common.back')}
              </Button>
            </Box>
          </Paper>
        ) : (
          <AvailabilityCalendar
            availableSlots={availableSlots}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            selectedSlot={selectedSlot}
            onSlotSelect={handleSlotSelect}
            loading={bookingLoading}
            minBookingDays={selectedCalendar?.booking_settings?.min_notice_hours ? 
              Math.ceil(selectedCalendar.booking_settings.min_notice_hours / 24) : 0}
            maxBookingDays={selectedCalendar?.booking_settings?.advance_booking_days || 30}
          />
        )
      )}

      {activeStep === 2 && (
        <BookingForm
          selectedSlot={selectedSlot}
          meetingType={selectedMeetingType}
          onSubmit={handleBookingSubmit}
          onCancel={handleBack}
          loading={bookingLoading}
          error={bookingError}
          userProfile={userProfile}
          rebookData={rebookData}
        />
      )}

      {activeStep === 3 && (
        <BookingConfirmation
          booking={{
            ...bookingResult,
            start_time: selectedSlot?.slot_start || selectedSlot?.start,
            end_time: selectedSlot?.slot_end || selectedSlot?.end,
          }}
          businessName={business.business_name}
          meetingType={selectedMeetingType}
          onBookAnother={handleBookAnother}
        />
      )}
    </Container>
  );
};

export default BookingPage;
